import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Option } from '../../../../shared/models/option.model';
import { BookingReportsService } from '../../services/booking-reports.service';
import { MasterService } from '../../../master/services/master.service';
import { MasterModel } from '../../../master/models/master.model';
import { formatLocalIso } from '../../utils/date.utils';
import {
  InformeBookingResponse,
  FiltroInformesBooking,
  InformeVentas,
  InformeEmpleados,
  InformeOperativo
} from '../../models/booking-reports.model';

@Component({
    selector: 'app-booking-reports',
    templateUrl: './booking-reports.component.html',
    styleUrls: ['./booking-reports.component.scss'],
    standalone: false
})
export class BookingReportsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Reactive state
  loading = false;
  error: string | null = null;
  activeTab: 'sales' | 'employees' | 'operational' = 'sales';

  // Filters
  fromDate: string;
  toDate: string;
  locationId = '';
  staffId = '';
  conceptId = '';
  statusIds: number[] = [];

  fromDateControl = new UntypedFormControl('');
  toDateControl = new UntypedFormControl('');
  locationControl = new UntypedFormControl(null);
  conceptControl = new UntypedFormControl(null);
  staffControl = new UntypedFormControl(null);

  locationOptions: Option[] = [];
  conceptOptions: Option[] = [];
  staffOptions: Option[] = [];

  locationAutocompleteConfig: any = {
    label: 'Centro de Trabajo',
    placeholder: 'Seleccionar centro...',
    options: this.locationOptions,
    onChange: (value: Option) => this.onLocationOptionChange(value)
  };

  conceptAutocompleteConfig: any = {
    label: 'Servicio',
    placeholder: 'Seleccionar servicio...',
    options: this.conceptOptions,
    onChange: (value: Option) => this.onConceptOptionChange(value)
  };

  staffAutocompleteConfig: any = {
    label: 'Empleado',
    placeholder: 'Seleccionar empleado...',
    options: this.staffOptions,
    onChange: (value: Option) => this.onStaffOptionChange(value)
  };

  // Master lists for dropdowns
  locations: MasterModel[] = [];
  staff: MasterModel[] = [];
  concepts: MasterModel[] = [];

  // Data
  reports: InformeBookingResponse | null = null;
  salesReport: InformeVentas | null = null;
  employeesReport: InformeEmpleados | null = null;
  operationalReport: InformeOperativo | null = null;

  // Status options
  availableStatuses = [
    { id: 1, name: 'Completada' },
    { id: 2, name: 'Cancelada' },
    { id: 3, name: 'No presentada' },
    { id: 4, name: 'Confirmada' },
    { id: 5, name: 'Pendiente' }
  ];

  constructor(
    private reportsApi: BookingReportsService,
    private masterService: MasterService,
    private snackBar: MatSnackBar
  ) {
    this.fromDate = this.getDefaultFromDate();
    this.toDate = this.getDefaultToDate();
    this.fromDateControl.setValue(this.fromDate);
    this.toDateControl.setValue(this.toDate);
  }

  ngOnInit(): void {
    this.loadLocations();
    this.loadReports();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadLocations(): void {
    this.masterService.getMerchantLocations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.locations = data;
          this.locationOptions = this.toOptions(data);
          this.locationAutocompleteConfig.options = this.locationOptions;
        },
        () => {
          this.locations = [];
          this.locationOptions = [];
          this.locationAutocompleteConfig.options = this.locationOptions;
        }
      );
  }

  onLocationOptionChange(value: Option): void {
    this.locationId = value && value.id ? value.id : '';
    this.onLocationChange();
  }

  onConceptOptionChange(value: Option): void {
    this.conceptId = value && value.id ? value.id : '';
    this.onConceptChange();
  }

  onStaffOptionChange(value: Option): void {
    this.staffId = value && value.id ? value.id : '';
  }

  onLocationChange(): void {
    this.staffId = '';
    this.conceptId = '';
    this.staff = [];
    this.concepts = [];
    this.staffControl.setValue(null, { emitEvent: false });
    this.conceptControl.setValue(null, { emitEvent: false });
    this.staffOptions = [];
    this.conceptOptions = [];
    this.staffAutocompleteConfig.options = this.staffOptions;
    this.conceptAutocompleteConfig.options = this.conceptOptions;

    if (!this.locationId) return;

    this.masterService.getConceptsByLocation(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.concepts = data;
          this.conceptOptions = this.toOptions(data);
          this.conceptAutocompleteConfig.options = this.conceptOptions;
        },
        () => {
          this.concepts = [];
          this.conceptOptions = [];
          this.conceptAutocompleteConfig.options = this.conceptOptions;
        }
      );

    this.masterService.getEmployeesByLocationAndConcept(this.locationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.staff = data;
          this.staffOptions = this.toOptions(data);
          this.staffAutocompleteConfig.options = this.staffOptions;
        },
        () => {
          this.staff = [];
          this.staffOptions = [];
          this.staffAutocompleteConfig.options = this.staffOptions;
        }
      );
  }

  onConceptChange(): void {
    this.staffId = '';
    this.staff = [];
    this.staffControl.setValue(null, { emitEvent: false });
    this.staffOptions = [];
    this.staffAutocompleteConfig.options = this.staffOptions;

    if (!this.locationId) return;

    this.masterService.getEmployeesByLocationAndConcept(this.locationId, this.conceptId || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          this.staff = data;
          this.staffOptions = this.toOptions(data);
          this.staffAutocompleteConfig.options = this.staffOptions;
        },
        () => {
          this.staff = [];
          this.staffOptions = [];
          this.staffAutocompleteConfig.options = this.staffOptions;
        }
      );
  }

  setActiveTab(tab: 'sales' | 'employees' | 'operational'): void {
    this.activeTab = tab;
  }

  loadReports(): void {
    this.loading = true;
    this.error = null;
    this.fromDate = this.fromDateControl.value || this.fromDate;
    this.toDate = this.toDateControl.value || this.toDate;

    const filter: FiltroInformesBooking = {
      desde: this.fromDate,
      hasta: this.toDate,
      centroTrabajoId: this.locationId || undefined,
      empleadoId: this.staffId || undefined,
      servicioId: this.conceptId || undefined,
      estadoIds: this.statusIds.length > 0 ? this.statusIds : undefined
    };

    this.reportsApi.getBookingReports(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response.isSuccess && response.value) {
            const mapped = this.mapReportsResponse(response.value);
            this.reports = mapped;
            this.salesReport = mapped.informeVentas;
            this.employeesReport = mapped.informeEmpleados;
            this.operationalReport = mapped.informeOperativo;
          } else {
            this.error = (response.error && response.error.messages && response.error.messages[0]) || 'Error al cargar informes';
          }
          this.loading = false;
        },
        (err) => {
          console.error('Error al cargar informes:', err);
          this.error = 'No se pudieron cargar los informes';
          this.loading = false;
          this.snackBar.open(this.error, 'Cerrar', { duration: 5000 });
        }
      );
  }

  toggleStatus(statusId: number): void {
    const index = this.statusIds.indexOf(statusId);
    if (index > -1) {
      this.statusIds.splice(index, 1);
    } else {
      this.statusIds.push(statusId);
    }
  }

  isStatusSelected(statusId: number): boolean {
    return this.statusIds.indexOf(statusId) > -1;
  }

  resetFilters(): void {
    this.fromDate = this.getDefaultFromDate();
    this.toDate = this.getDefaultToDate();
    this.fromDateControl.setValue(this.fromDate, { emitEvent: false });
    this.toDateControl.setValue(this.toDate, { emitEvent: false });
    this.locationId = '';
    this.staffId = '';
    this.conceptId = '';
    this.locationControl.setValue(null, { emitEvent: false });
    this.staffControl.setValue(null, { emitEvent: false });
    this.conceptControl.setValue(null, { emitEvent: false });
    this.statusIds = [];
    this.staff = [];
    this.concepts = [];
    this.staffOptions = [];
    this.conceptOptions = [];
    this.staffAutocompleteConfig.options = this.staffOptions;
    this.conceptAutocompleteConfig.options = this.conceptOptions;
    this.loadReports();
  }

  exportToExcel(): void {
    const filter: FiltroInformesBooking = {
      desde: this.fromDateControl.value || this.fromDate,
      hasta: this.toDateControl.value || this.toDate,
      centroTrabajoId: this.locationId || undefined,
      empleadoId: this.staffId || undefined,
      servicioId: this.conceptId || undefined,
      estadoIds: this.statusIds.length > 0 ? this.statusIds : undefined
    };

    this.reportsApi.exportBookingReportsExcel(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          const blob = response.body;
          if (!blob) {
            this.snackBar.open('No se pudo generar el archivo Excel', 'Cerrar', { duration: 4000 });
            return;
          }

          const contentDisposition = response.headers.get('content-disposition') || '';
          const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
          const fileName = decodeURIComponent((fileNameMatch && (fileNameMatch[1] || fileNameMatch[2])) || 'booking-reports.xlsx');

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);

          this.snackBar.open('Excel exportado correctamente', 'Cerrar', { duration: 3000 });
        },
        () => {
          this.snackBar.open('Error al exportar el Excel', 'Cerrar', { duration: 5000 });
        }
      );
  }

  private getDefaultFromDate(): string {
    const date = new Date();
    date.setDate(1);
    return this.formatDate(date);
  }

  private getDefaultToDate(): string {
    return this.formatDate(new Date());
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toOptions(items: MasterModel[]): Option[] {
    return items.map(x => ({ id: x.id, display: x.display }));
  }

  private mapReportsResponse(raw: any): InformeBookingResponse {
    // Backward compatibility with old shape already mapped to Spanish keys
    if (raw && raw.informeVentas && raw.informeEmpleados && raw.informeOperativo) {
      return raw as InformeBookingResponse;
    }

    return {
      informeVentas: {
        kpis: {
          ventasTotales: raw && raw.sales && raw.sales.kpis ? (raw.sales.kpis.totalSales || 0) : 0,
          citasCompletadas: raw && raw.sales && raw.sales.kpis ? (raw.sales.kpis.completedAppointments || 0) : 0,
          clientesAtendidos: raw && raw.sales && raw.sales.kpis ? (raw.sales.kpis.customersAttended || 0) : 0,
          ticketPromedio: raw && raw.sales && raw.sales.kpis ? (raw.sales.kpis.averageTicket || 0) : 0
        },
        filas: raw && raw.sales && raw.sales.rows ? raw.sales.rows.map((r: any) => ({
          citaId: r && r.bookingId ? r.bookingId : '',
          inicio: r && r.date ? r.date : '',
          fin: r && r.date && r.durationMinutes ? formatLocalIso(new Date(new Date(r.date).getTime() + (r.durationMinutes * 60000))) : '',
          nombreCliente: r && r.customer ? r.customer : '',
          nombreEmpleado: r && r.employee ? r.employee : '',
          nombreServicio: r && r.service ? r.service : '',
          precio: r && r.amount ? r.amount : 0,
          estado: r && r.status ? r.status : ''
        })) : [],
        resumenPorEmpleado: raw && raw.sales && raw.sales.summaryByEmployee ? raw.sales.summaryByEmployee.map((r: any) => ({
          empleadoId: r && r.staffId ? r.staffId : '',
          nombreEmpleado: r && r.employee ? r.employee : '',
          ventasTotales: r && r.totalSales ? r.totalSales : 0,
          citasCompletadas: r && r.completedAppointments ? r.completedAppointments : 0,
          clientesAtendidos: r && r.customersAttended ? r.customersAttended : 0,
          ticketPromedio: r && r.averageTicket ? r.averageTicket : 0
        })) : []
      },
      informeEmpleados: {
        kpis: {
          ventasTotales: raw && raw.employees && raw.employees.kpis ? (raw.employees.kpis.totalSales || 0) : 0,
          horasOcupadas: raw && raw.employees && raw.employees.kpis ? (raw.employees.kpis.occupiedHours || 0) : 0,
          horasLibres: raw && raw.employees && raw.employees.kpis ? (raw.employees.kpis.freeHours || 0) : 0,
          porcentajeOcupacion: raw && raw.employees && raw.employees.kpis ? (raw.employees.kpis.occupancyPercent || 0) : 0
        },
        filas: raw && raw.employees && raw.employees.rows ? raw.employees.rows.map((r: any) => ({
          empleadoId: r && r.staffId ? r.staffId : '',
          nombreEmpleado: r && r.employee ? r.employee : '',
          ventasTotales: r && r.sales ? r.sales : 0,
          horasOcupadas: r && r.occupiedHours ? r.occupiedHours : 0,
          horasLibres: r && r.freeHours ? r.freeHours : 0,
          porcentajeOcupacion: r && r.occupancyPercent ? r.occupancyPercent : 0,
          revenuePerHora: r && r.revenuePerHour ? r.revenuePerHour : 0
        })) : []
      },
      informeOperativo: {
        kpis: {
          citasCompletadas: raw && raw.operational && raw.operational.kpis ? (raw.operational.kpis.completedAppointments || 0) : 0,
          citasCanceladas: raw && raw.operational && raw.operational.kpis ? (raw.operational.kpis.cancelledAppointments || 0) : 0,
          citasNoPresentadas: raw && raw.operational && raw.operational.kpis ? (raw.operational.kpis.noShowAppointments || 0) : 0,
          horasVacias: raw && raw.operational && raw.operational.kpis ? (raw.operational.kpis.emptyHours || 0) : 0
        },
        filas: raw && raw.operational && raw.operational.rows ? raw.operational.rows.map((r: any) => ({
          empleadoId: r && r.staffId ? r.staffId : '',
          nombreEmpleado: r && r.employee ? r.employee : '',
          citasCompletadas: r && r.completedAppointments ? r.completedAppointments : 0,
          citasCanceladas: r && r.cancelledAppointments ? r.cancelledAppointments : 0,
          citasNoPresentadas: r && r.noShowAppointments ? r.noShowAppointments : 0,
          horasVacias: r && r.emptyHours ? r.emptyHours : 0
        })) : []
      }
    };
  }
}
